import type React from 'react';
import { memo, useMemo } from 'react';
import { graphql } from 'react-apollo';

import { compose } from 'recompose';

import { enrichCatalogResults } from 'bundles/browse/components/CatalogResultUtils';
import type { EnrichedCatalogResultQuery } from 'bundles/browse/components/CatalogResultUtils';
import withPremiumProductCollections from 'bundles/browse/components/Domain/withPremiumProductCollections';
import { PRODUCT_TYPE_NAMES, ProductVariantNames } from 'bundles/browse/constants';
import type { DegreeProductMember } from 'bundles/browse/types/degree-list';
import DegreesDiscoveryExperiments from 'bundles/epic/clients/DegreesDiscovery';
import GrowthDiscoveryExperiments from 'bundles/epic/clients/GrowthDiscovery';
import GrowthPremiumExperiments from 'bundles/epic/clients/GrowthPremium';
import { lohpCollectionQuery } from 'bundles/front-page/common/Queries';
import type {
  LOHPCollectionQuery,
  LOHPCollectionQueryVariables,
} from 'bundles/front-page/common/__generated__/LOHPCollectionQuery';
import { getGenAICollectionSets } from 'bundles/front-page/constants/genAICollectionSets';
import type { PropsFromWithMediaMaxWidthScreenTablet } from 'bundles/front-page/hoc/WithMediaMaxWidthTablet';
import { withMediaMaxWidthScreenTablet } from 'bundles/front-page/hoc/WithMediaMaxWidthTablet';
import withUserAgentFromContext from 'bundles/front-page/hoc/withUserAgentFromContext';
import useDiscoveryCollectionsData from 'bundles/front-page/hooks/useDiscoveryCollectionsData';
import useStackableCollectionData from 'bundles/front-page/hooks/useStackableCollectionData';

const MOST_POPULAR_CERTS_COLLECTION = {
  contextId: 'logged-out-most-popular-certificates',
  contextType: 'PAGE',
};

type InnerProps = {
  regionSlug?: string;
  language?: string;
  certificatesOnly?: boolean;
  skip?: boolean;
  children: (data: FrontPageCollectionsDataState) => JSX.Element | null;
};

type PropsFromPremiumProductCollections = {
  degreeListFromPremiumProductsCollection?: DegreeProductMember[];
  mastertrackListFromPremiumProductsCollection?: DegreeProductMember[];
  universityCertificateListFromPremiumProductsCollection?: DegreeProductMember[];
};

type PropsFromCollections = {
  data: LOHPCollectionQuery;
} & InnerProps;

type PropsFromGraphql = {
  data: Partial<LOHPCollectionQuery>;
} & InnerProps;

export type FrontPageCollectionsDataState = {
  collectionsLoading?: boolean;
  collections?: EnrichedCatalogResultQuery[];
  matches: boolean;
};

type Props = PropsFromGraphql & PropsFromWithMediaMaxWidthScreenTablet & PropsFromPremiumProductCollections;

const isNotNull = <T,>(el: T | null): el is T => el !== null;

const METADATA_KEY_REGEX = new RegExp(/:(:)?\w+/gi);

export const LOHP_PREMIUM_PRODUCT_COLLECTIONS_VARIABLES = {
  domains: null,
  productVariants: [
    ProductVariantNames.BachelorsDegree,
    ProductVariantNames.MastersDegree,
    ProductVariantNames.PostgraduateDiploma,
    ProductVariantNames.Mastertrack,
    ProductVariantNames.UniversityCertificate,
  ],
  ssr: true,
};

export const getCollectionKey = (collection: EnrichedCatalogResultQuery): string | null => {
  if (!collection || !collection.id) {
    return null;
  }

  const metadataKeys = collection?.id?.match(METADATA_KEY_REGEX);

  if (!metadataKeys || metadataKeys.length === 0) {
    return null;
  }

  return metadataKeys[metadataKeys.length - 1].replace(/[:]+/g, '');
};

export const immutableSplice = <T,>(arr: T[], replacementIndex: number, newElement: T): T[] => {
  return [...arr.slice(0, replacementIndex), newElement, ...arr.slice(replacementIndex + 1)];
};

export const immutableInsert = <T,>(collections: T[], index: number, newItems: T[]): T[] => {
  return index === -1
    ? [...collections, ...newItems]
    : [...collections.slice(0, index + 1), ...newItems, ...collections.slice(index + 1)];
};

export const getCollectionsData = (
  dataFromCollections: Partial<LOHPCollectionQuery>,
  certificatesOnly: boolean,
  degreesList?: DegreeProductMember[],
  reorderCollections?: boolean
) => {
  const nullResult = {
    collectionsData: [],
    collectionsLoading: true,
  };

  if (!dataFromCollections || !degreesList) {
    return nullResult;
  }

  const { BrowseCollectionsV1Resource } = dataFromCollections;

  if (!BrowseCollectionsV1Resource?.defaultCollections?.elements) {
    return nullResult;
  }

  const targettedCollections = BrowseCollectionsV1Resource.targettedCollections?.elements.filter(isNotNull) ?? [];
  const elements =
    targettedCollections.length > 0
      ? targettedCollections
      : BrowseCollectionsV1Resource.defaultCollections.elements.filter(isNotNull);

  // @ts-expect-error(apollo-v3): TS2345: Argument of type 'LOHPCollectionQuery_BrowseCollectionsV1Resource_target
  let collectionsData = enrichCatalogResults(elements);

  const degreeElements = degreesList;
  const degreeCollectionIndex = collectionsData?.map(getCollectionKey).indexOf('degrees');

  if (degreeElements && !certificatesOnly && degreeCollectionIndex !== -1) {
    const degreeList: DegreeProductMember[] = [];
    degreeElements.forEach((degreeProduct) => {
      if (degreeProduct?.partners?.elements && degreeList.length < 12) {
        degreeList.push({
          ...degreeProduct,
          type: PRODUCT_TYPE_NAMES.DEGREE,
          link: degreeProduct.link,
          partner: degreeProduct.partner,
        });
      }
    });

    // Merge degree entries with preset collection data
    const degrees = degreeList?.map((degree) => ({ degree, __typename: 'DegreeListsV3' }));

    const exploreDegreeCollection = {
      ...collectionsData[degreeCollectionIndex],
      entries: degrees,
      __typename: 'DegreeListsV3',
    } as unknown as EnrichedCatalogResultQuery;

    collectionsData = immutableSplice(collectionsData, degreeCollectionIndex, exploreDegreeCollection);
  }

  if (reorderCollections) {
    const freeCoursesCollection = collectionsData.find((collection) => getCollectionKey(collection) === 'COURSES');
    const degreeCollection = collectionsData.find((collection) => getCollectionKey(collection) === 'degrees');
    const newOnCourseraCollection = collectionsData.find((collection) => getCollectionKey(collection) === 'S18N');
    collectionsData = [newOnCourseraCollection, freeCoursesCollection, degreeCollection].filter(Boolean);
  }

  return {
    collectionsData,
    collectionsLoading: false,
  };
};

const WithCollectionDataInnerComponent: React.FC<Props> = memo(({ data, children, ...otherProps }) => {
  const {
    certificatesOnly,
    degreeListFromPremiumProductsCollection,
    mastertrackListFromPremiumProductsCollection,
    universityCertificateListFromPremiumProductsCollection,
  } = otherProps;
  const LOHPpathwayS12nGraduallyRolloutEpic = DegreesDiscoveryExperiments.preview('LOHPpathwayS12nGraduallyRollout');
  const prefetchGenAICollections = GrowthDiscoveryExperiments.preview('showMobileHeroSlider');
  const reorderCollections = GrowthDiscoveryExperiments.get('reorderLOHPCollections');

  const stackableData = useStackableCollectionData({
    skipDegree: false,
    skipS12n: !LOHPpathwayS12nGraduallyRolloutEpic,
  });

  const discoveryCollections = useDiscoveryCollectionsData({
    contextId: MOST_POPULAR_CERTS_COLLECTION.contextId,
    contextType: MOST_POPULAR_CERTS_COLLECTION.contextType,
    skip: !reorderCollections,
  });

  // prefetch GenAICollections
  const genAICollectionSets = getGenAICollectionSets();
  useDiscoveryCollectionsData({
    contextId: genAICollectionSets[0].contextId as string,
    contextType: genAICollectionSets[0].context as string,
    skip: !prefetchGenAICollections,
  });

  const { collectionsData, collectionsLoading } = useMemo(
    () =>
      getCollectionsData(data, certificatesOnly ?? false, degreeListFromPremiumProductsCollection, reorderCollections),
    [data, certificatesOnly, degreeListFromPremiumProductsCollection, reorderCollections]
  );

  let collections = [...collectionsData];

  // Need to get the degree and s18 index again
  let degreeCollectionIndex = collections?.map(getCollectionKey).indexOf('degrees');

  if (stackableData.stackableDegrees.length !== 0) {
    const stackableDegreesResultQuery: EnrichedCatalogResultQuery = {
      // @ts-expect-error - just temporarily ignore
      entries: stackableData.stackableDegrees.map((degree) => ({ degree })),
      label: '::STACKABLE_DEGREES',
      id: 'RECONFIGURABLE_COMPONENTS:stackable_degrees',
      linkedCollectionPageMetadata: null,
      debug: 'false',
      __typename: 'PremiumProductCollection',
    };

    // show the stackable degree module on top of the degree module
    if (reorderCollections) {
      collections = immutableInsert(collections, degreeCollectionIndex - 1, [stackableDegreesResultQuery]);
    } else {
      collections = [stackableDegreesResultQuery, ...collections];
    }
  }

  const s18nCollectionIndex = collections?.map(getCollectionKey).indexOf('S18N');

  if (stackableData.stackableSpecializations.length !== 0) {
    const stackableCoursesAndCertificatesResultQuery: EnrichedCatalogResultQuery = {
      // @ts-expect-error - just temporarily ignore
      entries: stackableData.stackableSpecializations.map((degree) => ({ degree })),
      label: '::STACKABLE_SPECIALIZATIONS',
      id: 'RECONFIGURABLE_COMPONENTS:stackable_specializations',
      linkedCollectionPageMetadata: null,
      debug: 'false',
      __typename: 'PremiumProductCollection',
    };

    if (reorderCollections) {
      // show stackable specializations on top of stackable degrees for Consumer Softness Levers Experiment
      const stackableDegreesIndex = collections?.map(getCollectionKey).indexOf('stackable_degrees');
      collections = immutableInsert(collections, stackableDegreesIndex + 1, [
        stackableCoursesAndCertificatesResultQuery,
      ]);
    } else {
      collections = immutableInsert(collections, s18nCollectionIndex, [stackableCoursesAndCertificatesResultQuery]);
    }
  }

  // Need to update the degree index since the stackable degree module is inserted on top
  degreeCollectionIndex = collections?.map(getCollectionKey).indexOf('degrees');

  if (GrowthPremiumExperiments.get('showMasterTracksAndUcertsOnHomepage') === true) {
    // TODO: add two collections below to LOHPCollectionsQuery
    // Manually adding mastertrack and ucert items for mastertrack and certificate discovery experiment
    const mastertrackCatalogResultQuery: EnrichedCatalogResultQuery = {
      // @ts-expect-error - just temporarily ignore
      entries: mastertrackListFromPremiumProductsCollection.map((degree) => ({ degree })),
      label: '::MASTERTRACKS',
      id: 'RECONFIGURABLE_COMPONENTS:mastertracks',
      linkedCollectionPageMetadata: null,
      debug: 'false',
      __typename: 'PremiumProductCollection',
    };

    const universityCertificateResultQuery: EnrichedCatalogResultQuery = {
      // @ts-expect-error - just temporarily ignore
      entries: universityCertificateListFromPremiumProductsCollection?.map((degree) => ({ degree })),
      label: '::UNIVERSITY_CERTIFICATES',
      id: 'RECONFIGURABLE_COMPONENTS:university_certificates',
      linkedCollectionPageMetadata: null,
      debug: 'false',
      __typename: 'PremiumProductCollection',
    };

    collections = immutableInsert(collections, degreeCollectionIndex, [
      mastertrackCatalogResultQuery,
      universityCertificateResultQuery,
    ]);
  }

  // Add Most Popular Certs collection on the top
  if (reorderCollections && discoveryCollections.collections) {
    const mostPopularCertsDiscoCollection = discoveryCollections.collections[0];
    if (mostPopularCertsDiscoCollection) {
      const mostPopularCertsCollection: EnrichedCatalogResultQuery = {
        // @ts-expect-error - just temporarily ignore
        entries: mostPopularCertsDiscoCollection.entities?.map((entity) => ({
          ...entity,
          partners: { elements: entity?.partners },
          logo: entity?.imageUrl,
          productVariant:
            entity?.__typename === 'DiscoveryCollections_professionalCertificate'
              ? 'ProfessionalCertificateS12n'
              : 'NormalS12n',
        })),
        label: mostPopularCertsDiscoCollection.label,
        id: mostPopularCertsDiscoCollection.id,
        linkedCollectionPageMetadata: null,
        debug: 'false',
        __typename: 'BrowseCollectionsV1',
      };
      collections = [mostPopularCertsCollection, ...collections];
    }
  }

  return children({
    ...otherProps,
    collectionsLoading,
    collections,
  });
});

export const getCollectionId = ({
  certificatesOnly,
  language,
  regionSlug,
}: Required<Pick<InnerProps, 'certificatesOnly' | 'language' | 'regionSlug'>>) => {
  const collectionIdParticles = ['lohp'];

  if (regionSlug === 'us') {
    collectionIdParticles.push('global');
    collectionIdParticles.push(language);
  } else {
    collectionIdParticles.push(regionSlug);
  }

  if (certificatesOnly) {
    collectionIdParticles.push('certificates');
  }

  return collectionIdParticles.join('-');
};

export const lohpDegreeListsV3Options = () => {
  const fetchPolicy = 'cache-and-network' as const;
  return { fetchPolicy };
};

export const getLohpCollectionQueryVariables = ({
  certificatesOnly,
  language = 'en',
  regionSlug = 'us',
}: Omit<InnerProps, 'children'>): LOHPCollectionQueryVariables => {
  const browseCollectionId = getCollectionId({
    certificatesOnly: certificatesOnly || false,
    language,
    regionSlug,
  });

  const defaultCollectionId = getCollectionId({
    certificatesOnly: certificatesOnly || false,
    language: 'en',
    regionSlug: 'us',
  });

  return {
    contextId: browseCollectionId,
    defaultCollectionId,
    isDefaultCollection: browseCollectionId === defaultCollectionId,
    numEntriesPerCollection: 12,
  };
};

WithCollectionDataInnerComponent.displayName = 'WithCollectionDataInnerComponent';

export const WithCollectionData = compose<Props, InnerProps>(
  graphql<InnerProps, LOHPCollectionQuery, LOHPCollectionQueryVariables, PropsFromCollections>(lohpCollectionQuery, {
    skip: ({ skip = false }) => skip,
    options: (props) => ({
      variables: getLohpCollectionQueryVariables(props),
      fetchPolicy: 'cache-first',
      ssr: true,
    }),
  }),
  withPremiumProductCollections(() => LOHP_PREMIUM_PRODUCT_COLLECTIONS_VARIABLES),
  withUserAgentFromContext(),
  withMediaMaxWidthScreenTablet
)(WithCollectionDataInnerComponent);
